import { HereMapClickEvent } from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import { logger } from '@user/utils/logger'

const apikey = process.env.REACT_HERE_API_KEY
const hereMapSearchBaseURL = process.env.REACT_APP_GEO_SEARCH_URL

export interface LatLng {
  lat: number
  lng: number
}

export const useApi = () => {
  const getAddressDetailsForMapClick = async (latLng: LatLng) => {
    try {
      const url = `${hereMapSearchBaseURL}?at=${latLng.lat},${latLng.lng}&limit=1&apiKey=${apikey}`
      const response = await fetch(url)
      if (response.ok) {
        const data = await response.json()
        const item = data.items?.[0]

        const resolvedLat = item?.position?.lat ?? latLng.lat
        const resolvedLng = item?.position?.lng ?? latLng.lng

        let locationData: HereMapClickEvent = {
          latitude: resolvedLat,
          longitude: resolvedLng,
        }

        if (item && item.address) {
          // Prefill when HERE identifies a specific house number at the click point.
          // The distance check is omitted because building polygon clicks land
          // inside the footprint, which can be 15–30 m from the road-side address
          // point HERE uses as its reference — the result type is a sufficient guard.
          if (item.address.houseNumber && item.resultType === 'houseNumber') {
            // Create custom label without country
            const addressParts = [
              item.address.houseNumber && item.address.street
                ? `${item.address.houseNumber} ${item.address.street}`
                : item.address.street || item.address.houseNumber,
              item.address.city,
              item.address.state,
              item.address.postalCode,
            ].filter(Boolean)
            const customLabel = addressParts.join(', ')

            locationData = {
              ...locationData,
              label: customLabel,
              houseNumber: item.address.houseNumber,
              street: item.address.street,
              city: item.address.city,
              state: item.address.stateCode || item.address.state,
              postalCode: item.address.postalCode,
              county: item.address.county,
              addressClick: true,
              address: {
                street1:
                  item.address.houseNumber && item.address.street
                    ? `${item.address.houseNumber} ${item.address.street}`
                    : item.address.street || item.address.houseNumber,
                city: item.address.city,
                stateAbbreviation: item.address.stateCode || item.address.state,
                postalCode: item.address.postalCode,
                country: item.address.countryName,
                latitude: resolvedLat,
                longitude: resolvedLng,
              },
            }
          } else {
            // No specific house, but we can still get city name.
            // Always use the geocoder's resolved position so knock status pins
            // snap to a canonical point and duplicates are detectable by coords.
            locationData = {
              ...locationData,
              city: item.address.city,
              address: {
                city: item.address.city,
                stateAbbreviation: item.address.stateCode || item.address.state,
                latitude: resolvedLat,
                longitude: resolvedLng,
              },
            }
            logger.log('Location found (no house):', {
              city: item.address.city,
              resultType: item.resultType,
              distance: item.distance,
            })
          }
        } else {
          logger.log('No location data found at this position')
        }

        return locationData
      } else {
        return undefined
      }
    } catch (error) {
      logger.error('Error reverse geocoding:', error)
      return undefined
    }
  }

  return { getAddressDetailsForMapClick }
}
